import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet } from '../services/api';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import RefreshButton from '../components/RefreshButton';
import { estaOnline, haQuanto, MINUTOS_ONLINE } from '../components/configuracoes/VisaoGeral';
import { includesNormalized } from '../utils/normalizeText';

/**
 * Configurações › Usuários e acessos: uma linha por conta, com o último
 * acesso e o uso nos últimos 30 dias. Os cartões da Visão Geral abrem esta
 * página já filtrada (`?situacao=`). Só administrador.
 */

const SITUACOES = [
  { value: '', label: 'Todas as contas' },
  { value: 'ativas', label: 'Contas ativas' },
  { value: 'online', label: `Online agora (últimos ${MINUTOS_ONLINE} min)` },
  { value: 'hoje', label: 'Acessaram hoje' },
  { value: '7d', label: 'Acessaram nos últimos 7 dias' },
  { value: '30d', label: 'Acessaram nos últimos 30 dias' },
  { value: 'sem30', label: 'Sem acesso há 30+ dias' },
  { value: 'nunca', label: 'Nunca acessaram' },
  { value: 'inativas', label: 'Contas inativas' },
];

const naSituacao = (u, situacao) => {
  const d = u.dias_sem_acesso;
  switch (situacao) {
    case 'ativas': return u.is_active;
    case 'online': return u.is_active && estaOnline(u.ultimo_acesso);
    case 'hoje': return u.is_active && d === 0;
    case '7d': return u.is_active && d !== null && d <= 6;
    case '30d': return u.is_active && d !== null && d <= 29;
    case 'sem30': return u.is_active && d !== null && d > 29;
    case 'nunca': return u.is_active && d === null;
    case 'inativas': return !u.is_active;
    default: return true;
  }
};

/** Cor do último acesso: verde até 1 dia, âmbar até 7, cinza até 30, vermelho depois. */
function corDoAcesso(dias) {
  if (dias === null) return { dot: 'bg-slate-400', rotulo: 'nunca' };
  if (dias <= 1) return { dot: 'bg-[#13983D]', rotulo: 'recente' };
  if (dias <= 7) return { dot: 'bg-[#F9B047]', rotulo: 'na semana' };
  if (dias <= 30) return { dot: 'bg-slate-500', rotulo: 'no mês' };
  return { dot: 'bg-[#E83A3C]', rotulo: 'há mais de 30 dias' };
}

const ORDENS = {
  nome: (a, b) => a.nome.localeCompare(b.nome, 'pt-BR'),
  // Quem acessou mais recentemente primeiro; quem nunca acessou por último.
  acesso: (a, b) => (a.dias_sem_acesso ?? 1e9) - (b.dias_sem_acesso ?? 1e9)
    || (b.ultimo_acesso || '').localeCompare(a.ultimo_acesso || ''),
  dias: (a, b) => b.dias_com_acesso_30d - a.dias_com_acesso_30d,
  acoes: (a, b) => b.acoes_30d - a.acoes_30d,
};

const POR_PAGINA = 50;

function baixarCsv(linhas) {
  const cab = ['Nome', 'Login', 'E-mail', 'Perfil', 'Município', 'Escolas', 'Situação da conta',
    'Último acesso', 'Dias com acesso (30d)', 'Ações salvas (30d)'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const corpo = linhas.map((u) => [
    u.nome, u.username, u.email, u.role_display, u.city || '', u.escolas.join('; '),
    u.is_active ? 'Ativa' : 'Inativa',
    u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString('pt-BR') : 'Nunca',
    u.dias_com_acesso_30d, u.acoes_30d,
  ].map(esc).join(';'));
  const blob = new Blob([`﻿${[cab.map(esc).join(';'), ...corpo].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `usuarios-e-acessos-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function UsuariosAcessos() {
  const { darkMode } = useTheme();
  const cidade = useEffectiveCity();
  const [params, setParams] = useSearchParams();

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [perfil, setPerfil] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [ordem, setOrdem] = useState('acesso');
  const [pagina, setPagina] = useState(1);
  const situacao = params.get('situacao') || '';

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDados(await apiGet('/system-stats/usuarios/', cidade?.id ? { city: cidade.id } : {}));
    } catch (err) {
      setErro(err?.message || 'Não foi possível carregar os usuários.');
    } finally {
      setCarregando(false);
    }
  }, [cidade?.id]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setPagina(1); }, [busca, perfil, municipio, situacao, ordem]);

  const usuarios = useMemo(() => dados?.usuarios || [], [dados]);
  const perfis = useMemo(
    () => [...new Map(usuarios.map((u) => [u.role, u.role_display])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR')),
    [usuarios]
  );
  const municipios = useMemo(
    () => [...new Set(usuarios.map((u) => u.city || 'Sem município'))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [usuarios]
  );

  const filtrados = useMemo(() => usuarios
    .filter((u) => naSituacao(u, situacao))
    .filter((u) => !perfil || u.role === perfil)
    .filter((u) => !municipio || (u.city || 'Sem município') === municipio)
    .filter((u) => !busca || [u.nome, u.username, u.email, ...u.escolas].some((t) => includesNormalized(t, busca)))
    .sort(ORDENS[ordem]), [usuarios, situacao, perfil, municipio, busca, ordem]);

  const paginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const visiveis = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const suave = darkMode ? 'text-slate-400' : 'text-slate-500';
  const linha = darkMode ? 'border-slate-700/60' : 'border-slate-100';
  const campo = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
    ? 'bg-slate-800 border-slate-700 text-slate-100'
    : 'bg-white border-slate-200 text-slate-700'}`;
  const caixa = `rounded-2xl ${darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`;

  const Cabecalho = ({ id, children, alinhar = 'text-left' }) => (
    <th className={`px-3 py-2 ${alinhar}`}>
      <button type="button" onClick={() => setOrdem(id)}
        className={`text-[11px] font-semibold uppercase tracking-wide ${ordem === id ? 'text-[#0974BA]' : suave}`}>
        {children}{ordem === id ? ' ▾' : ''}
      </button>
    </th>
  );

  if (carregando && !dados) return <LoadingSpinnerAdvanced message="Carregando usuários e acessos..." />;

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6">
        <div>
          <Link to="/configuracoes" className="text-sm text-[#0974BA] hover:underline">← Configurações</Link>
          <h1 className={`mt-1 text-2xl md:text-3xl font-medium ${forte}`}>Usuários e acessos</h1>
          <p className={`mt-1 text-sm ${suave}`}>
            Último acesso e uso nos últimos {dados?.dias || 30} dias — {cidade?.name || 'todos os municípios'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => baixarCsv(filtrados)} disabled={!filtrados.length}
            className={`px-4 py-2 rounded-lg text-sm font-medium border disabled:opacity-50 ${darkMode
              ? 'border-slate-700 text-slate-200 hover:bg-slate-700/60' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            Baixar planilha (CSV)
          </button>
          <RefreshButton onClick={carregar} />
        </div>
      </header>

      {erro && <div className="mb-4 px-4 py-3 rounded-xl text-sm bg-[#E83A3C]/10 text-[#E83A3C] border border-[#E83A3C]/25">{erro}</div>}

      <div className={`${caixa} p-4 mb-4 flex flex-wrap gap-3`}>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, login, e-mail ou escola..."
          className={`${campo} flex-1 min-w-[220px]`} />
        <select value={situacao} onChange={(e) => setParams(e.target.value ? { situacao: e.target.value } : {})}
          className={campo} aria-label="Situação">
          {SITUACOES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={perfil} onChange={(e) => setPerfil(e.target.value)} className={campo} aria-label="Perfil">
          <option value="">Todos os perfis</option>
          {perfis.map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
        </select>
        {municipios.length > 1 && (
          <select value={municipio} onChange={(e) => setMunicipio(e.target.value)} className={campo} aria-label="Município">
            <option value="">Todos os municípios</option>
            {municipios.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>

      <p className={`mb-2 text-sm ${suave}`}>
        {filtrados.length} {filtrados.length === 1 ? 'conta' : 'contas'}
        {situacao && ` · ${SITUACOES.find((s) => s.value === situacao)?.label.toLowerCase()}`}
      </p>

      <div className={`${caixa} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${linha}`}>
              <Cabecalho id="nome">Pessoa</Cabecalho>
              <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${suave}`}>Perfil · município</th>
              <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${suave}`}>Escolas</th>
              <Cabecalho id="acesso">Último acesso</Cabecalho>
              <Cabecalho id="dias" alinhar="text-right">Dias com acesso</Cabecalho>
              <Cabecalho id="acoes" alinhar="text-right">Ações salvas</Cabecalho>
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 && (
              <tr><td colSpan={6} className={`px-3 py-8 text-center ${suave}`}>Nenhuma conta com esses filtros.</td></tr>
            )}
            {visiveis.map((u) => {
              const cor = corDoAcesso(u.dias_sem_acesso);
              const online = u.is_active && estaOnline(u.ultimo_acesso);
              return (
                <tr key={u.id} className={`border-b last:border-0 ${linha} ${u.is_active ? '' : 'opacity-60'}`}>
                  <td className="px-3 py-2">
                    <p className={`font-medium ${forte}`}>{u.nome}</p>
                    <p className={`text-xs ${suave}`}>@{u.username}{u.is_active ? '' : ' · inativa'}</p>
                  </td>
                  <td className="px-3 py-2">
                    <p className={forte}>{u.role_display}</p>
                    <p className={`text-xs ${suave}`}>{u.city || 'Sem município'}</p>
                  </td>
                  <td className={`px-3 py-2 text-xs ${suave}`}>
                    {u.escolas.length ? (
                      <>{u.escolas.slice(0, 2).join(', ')}{u.escolas.length > 2 ? ` +${u.escolas.length - 2}` : ''}</>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${online ? 'bg-[#13983D] ring-2 ring-[#13983D]/30' : cor.dot}`}
                        title={online ? 'online agora' : cor.rotulo} />
                      <span className={forte}>{online ? 'online' : haQuanto(u.ultimo_acesso)}</span>
                    </span>
                    {u.ultimo_acesso && (
                      <p className={`text-[11px] ${suave}`}>{new Date(u.ultimo_acesso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${forte}`}>
                    {u.dias_com_acesso_30d}<span className={`text-[11px] ${suave}`}> de {dados.dias}</span>
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${forte}`}>{u.acoes_30d}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {paginas > 1 && (
        <div className={`mt-3 flex items-center justify-end gap-2 text-sm ${suave}`}>
          <button type="button" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}
            className="px-3 py-1 rounded-lg disabled:opacity-40 hover:text-[#0974BA]">‹ Anterior</button>
          <span>Página {pagina} de {paginas}</span>
          <button type="button" disabled={pagina === paginas} onClick={() => setPagina((p) => p + 1)}
            className="px-3 py-1 rounded-lg disabled:opacity-40 hover:text-[#0974BA]">Próxima ›</button>
        </div>
      )}
    </>
  );
}

export default UsuariosAcessos;
